/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import * as d3 from 'd3';
import _, { uniqueId } from 'lodash';
import moment, { Moment } from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';

import { Axis, CHAR_HEIGHT } from 'src/components/charts/helpers/Axis';
import {
  defaultFormatValue,
  useXTimeAxisTicks,
  useYAxisTicks,
} from 'src/components/charts/helpers/axisTicks';
import {
  useLinearScale,
  useTimeScale,
} from 'src/components/charts/helpers/useScale';
import { getTimestampsInRange } from 'src/lib/date/date';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { Resolution } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { Cursor } from '../helpers/Cursor';
import { SvgWrapper, useSvgLayout } from '../helpers/svgLayout';
import { useCursorDateIndex } from '../helpers/useCursorDateIndex';
import { StackConfig } from '../helpers/useStackInfo';
import { YAxisLabel } from '../helpers/YAxisLabel';

import { Key, Legend } from './legend/Legend';
import { LineChartTooltip } from './LineChartTooltip';

type StackCount = { value: number; stackId: number } | null;
export type LineChartDataRecord = {
  timestamp: Date;
  majorStackCounts: StackCount[];
  minorStackCounts: StackCount[];
};

const margins = { top: 5, left: 0, right: 15, bottom: 20 };

/**
 * @template Data
 *
 * Configuration object for rendering line charts with stacks.
 *
 * @extends {StackConfig<Data, { timestamp: Timestamp['output']; value: number }>}
 *
 * @property {string} [colorGradientTop] - Optional top color for the gradient fill below the line.
 * @property {string} [colorGradientBottom] - Optional bottom color for the gradient fill below the line.
 * @property {string} primaryColor - Primary color used for the line and points in the line chart.
 *
 * Data Requirements:
 * The `Data` type used for the data array should meet certain conditions for the line chart to render successfully:
 * 1. `timestamp`: Required field to map the X-axis of the line chart.
 * 2. `getValue(data: Data)`: A function compatible with the data objects that extracts a numerical value to be plotted along the Y-axis.
 *
 * Example Structure for getValue:
 * ```typescript
 * export interface DataPoint {
 *   timestamp: Timestamp; // UNIX timestamp
 *   value: number; // Value for the Y-axis
 * }
 * ```
 *
 * Optional Functions:
 * 1. `hasData`: Determines whether a particular data point in the data array has relevant data to be plotted.
 * 2. `getValue`: Extracts the value to be plotted for a given data point.
 * 3. `label`: Provides a label for the data point that is plotted.
 * 4. `unit`: Specifies the unit for the data point that is plotted.
 */
export interface LineChartStackConfig<Data extends {}>
  extends StackConfig<Data, { timestamp: Timestamp['output']; value: number }> {
  colorGradientTop?: string;
  colorGradientBottom?: string;
  primaryColor: string;
}

/**
 * Generates rendering information for a single stack in a line chart.
 *
 * @template Data - Generic data type extending an empty object.
 *
 * @param {Object} params - Parameters needed to generate rendering information.
 * @param {number} params.stackId - The ID for the stack to be rendered.
 * @param {'minor' | 'major'} params.axis - Specifies if the stack is part of the minor or major axis.
 * @param {d3.ScaleLinear<number, number>} params.yScale - d3 scale for the Y-axis.
 * @param {d3.ScaleTime<number, number>} params.xScale - d3 scale for the X-axis.
 * @param {LineChartDataRecord[]} params.series - Data series to be plotted.
 * @param {number} params.height - Height of the chart container.
 * @param {string} [params.colorGradientTop] - Optional gradient color at the top of the stack.
 * @param {string} [params.colorGradientBottom] - Optional gradient color at the bottom of the stack.
 * @param {string} params.primaryColor - Primary color for the stack line.
 *
 * @returns {Object}
 * - key: Unique key for the stack, generated using `stackId` and `axis`.
 * - colorGradientTop: Top gradient color for the stack.
 * - colorGradientBottom: Bottom gradient color for the stack.
 * - primaryColor: Primary color for the stack line.
 * - line: d3 line generator.
 * - d3LinePath: SVG path data for the line chart.
 * - areaPath: SVG path data for the area under the line.
 * - clipAreaPath: SVG path data for the clipped area under the line.
 */
function getRenderedStackInfo<Data extends {}>({
  colorGradientTop,
  colorGradientBottom,
  primaryColor,
  cursorColor,
  stackId,
  axis,
  yScale,
  xScale,
  series,
  height,
}: {
  stackId: number;
  axis: 'minor' | 'major';
  yScale: d3.ScaleLinear<number, number>;
  xScale: d3.ScaleTime<number, number>;
  series: LineChartDataRecord[];
  height: number;
} & Omit<LineChartStackConfig<Data>, 'hasData' | 'getValue' | 'data'>) {
  const line = d3
    .line<LineChartDataRecord>()
    .x((bucket) => xScale(bucket.timestamp))
    .y((bucket) => yScale(bucket[`${axis}StackCounts`][stackId]?.value ?? 0))
    .curve(d3.curveLinear);

  const filteredData = series.filter(
    (bucket) => bucket[`${axis}StackCounts`][stackId],
  );
  return {
    key: `${stackId}${axis}`,
    colorGradientTop,
    colorGradientBottom,
    primaryColor,
    cursorColor,
    line,
    d3LinePath: line(filteredData) ?? '',
    areaPath:
      d3.area<LineChartDataRecord>().x(line.x()).y1(line.y())(filteredData) ??
      '',
    clipAreaPath:
      d3
        .area<LineChartDataRecord>()
        .y0(height)
        .x(line.x())
        .y1(line.y())
        .curve(d3.curveLinear)(filteredData) ?? '',
  };
}

/**
 * @template Data
 *
 * Custom hook interface for generating stack data to be used in Cloud Metrics line charts.
 *
 * @property {LineChartStackConfig<Data>[]} stacks - An array of stack configurations to be plotted in the line chart.
 * @property {boolean[]} [activeSeries] - Optional array of booleans indicating the active series on the chart.
 * @property {Resolution} resolution - Resolution setting for the chart.
 * @property {boolean} stacked - Flag indicating whether the line chart should be stacked.
 *
 * Stack Configuration Shape:
 * Each object in the `stacks` array must conform to the `LineChartStackConfig<Data>` interface.
 * ```typescript
 * LineChartStackConfig<Data> {
 *   data: Data[]; // Array of data objects
 *   getValue: (data: Data) => { timestamp: Timestamp; value: number }; // Function to extract plotting value from data
 *   hasData?: (data: Data) => boolean; // Optional function to determine if a data object should be plotted
 *   label?: string; // Optional label for the stack
 *   unit?: string; // Optional unit for the data
 *   primaryColor: string; // Primary color for the line
 *   colorGradientTop?: string; // Optional top gradient color
 *   colorGradientBottom?: string; // Optional bottom gradient color
 * }
 * ```
 *
 * Usage Example:
 * ```jsx
 * const stacks = [
 *   {
 *     data: [
 *       { timestamp: '2023-01-01T00:00:00Z', customField: 10 },
 *       { timestamp: '2023-01-02T00:00:00Z', customField: 15 },
 *       // ...
 *     ],
 *     getValue: (data) => ({ timestamp: data.timestamp, value: data.customField }),
 *     hasData: (data) => data.customField !== null,
 *     label: 'Series 1',
 *     unit: 'Unit 1',
 *     primaryColor: 'red',
 *     colorGradientTop: 'lightcoral',
 *     colorGradientBottom: 'darkred',
 *   },
 *   // ...
 * ];
 *
 * <LineChart
 *   stacked
 *   stacks={stacks}
 *   comparisonLine={comparisonLine}
 *   yAxisLabel={` ${metricPair.yAxisLabel}`}
 *   className="mx-2 mb-2 h-40"
 *   resolution={resolution}
 *   showLegend
 *   strokeWidth={1}
 * />
 * ```
 */
interface StackDataProps<Data extends {}> {
  stacks: LineChartStackConfig<Data>[];
  activeSeries?: boolean[];
  resolution: Resolution;
  stacked: boolean;
}

/**
 * Adds stack data to a given cache for rendering line chart stacks.
 *
 * @template Data - Generic data type extending an empty object.
 *
 * @param {Object} params - Parameters for adding stacks to data.
 * @param {Record<string, LineChartDataRecord>} params.cache - The data cache to which stack data will be added.
 * @param {'major' | 'minor'} params.axis - Specifies if the stacks are part of the major or minor axis.
 * @param {LineChartStackConfig<Data>[]} params.stacks - Array of stack configurations.
 * @param {boolean[]} [params.activeSeries] - Optional array to specify which series are active.
 * @param {Resolution} params.resolution - The resolution for the chart.
 * @param {boolean} params.stacked - Specifies if the stacks are to be rendered in a stacked manner.
 *
 * @returns {number} - The maximum value among all processed stacks. Useful for setting the y-axis scale.
 *
 * @example
 *
 * const maxValue = addStacksData({
 *   cache: someCache,
 *   axis: 'major',
 *   stacks: someStacks,
 *   activeSeries: someActiveSeries,
 *   resolution: someResolution,
 *   stacked: true
 * });
 */
function addStacksData<Data extends {}>({
  stacks,
  activeSeries,
  resolution,
  cache,
  stacked,
  axis,
}: {
  cache: Record<string, LineChartDataRecord>;
  axis: 'major' | 'minor';
} & StackDataProps<Data>) {
  let maxValue = 0;
  stacks.forEach(({ data, getValue }, stackId) => {
    (!activeSeries || activeSeries[stackId] ? data : [])?.forEach(
      (bucket, bucketIndex, arr) => {
        const { timestamp, value } = getValue(bucket);

        const lastValue = arr[bucketIndex - 1];
        if (lastValue) {
          // The stats schema wasn't returning empty buckets, but we need the 0 data points to properly plot the chart and handle the tooltip.
          // this will fill in the empty buckets. We can remove this if we ensure all stats schema will return every bucket.
          getTimestampsInRange(
            getValue(lastValue).timestamp,
            timestamp,
            resolution,
          ).forEach((zeroTimestamp) => {
            // eslint-disable-next-line no-param-reassign
            cache[zeroTimestamp] = cache[zeroTimestamp] ?? {
              timestamp: new Date(zeroTimestamp),
              minorStackCounts: stacks.map(() => null),
              majorStackCounts: stacks.map(() => null),
              [`${axis}StackCounts`]: stacks.map((_stack, i) =>
                i === stackId || stacked
                  ? { value: 0, label: '0', stackId: i }
                  : null,
              ),
            };
          });
        }
        const stackCounts =
          cache[timestamp]?.[`${axis}StackCounts`] ??
          stacks.map((_stack, i) =>
            stacked ? { value: 0, label: '0', stackId: i } : null,
          );
        stackCounts.forEach((stackCount, i) => {
          if (stacked ? i <= stackId : i === stackId) {
            const obj = stackCount ?? {
              value: 0,
              label: '0',
              stackId: i,
            };
            stackCounts[i] = obj;
            obj.value += value;
            maxValue = Math.max(maxValue, obj.value);
          }
        });
        // eslint-disable-next-line no-param-reassign
        cache[timestamp] = {
          timestamp: new Date(timestamp),
          majorStackCounts:
            cache[timestamp]?.majorStackCounts ?? stacks.map(() => null),
          minorStackCounts:
            cache[timestamp]?.minorStackCounts ?? stacks.map(() => null),
          [`${axis}StackCounts`]: stackCounts,
        };
      },
    );
  });
  return maxValue;
}

const defaultLineChartFormatValue = (
  _axis: 'major' | 'minor',
  value: number,
  maxValue: number,
) => defaultFormatValue(value, maxValue);

/**
 * LineChart is a versatile component designed for displaying time-series data.
 * It supports both stacked and non-stacked line chart visualizations.
 * Additional features include customizable axis labels and legends, dynamic tick intervals, comparison lines, and more.
 *
 * @param className - Optional CSS class to apply to the chart container
 * @param strokeWidth - Width of the lines in the chart
 * @param majorStacks - Configurations for the primary (major) line stacks
 * @param minorStacks - Configurations for the secondary (minor) line stacks
 * @param majorActiveSeries - An array of booleans to indicate which major stacks are active
 * @param minorActiveSeries - An array of booleans to indicate which minor stacks are active
 * @param firstTickValue - Moment object specifying the first tick value
 * @param tickIntervalMinutes - Interval between ticks on the X-axis, in minutes
 * @param yAxisLabel - Label for the Y-axis
 * @param comparisonLine - Configurations for the comparison line
 * @param formatValue - Function to format values on the Y-axis
 * @param allowSingleXAxisValue - Flag to allow or disallow rendering when there's only a single point on the X-axis
 * @param showLegend - Flag to show or hide the legend
 * @param dataTestId - Data-test attribute for the chart
 * @param tooltipComponent - The component used for the Tooltip
 * @param hideCursorArrow - Whether or not to hide the arrow on the cursor tooltip
 */
export function LineChart<MajorData extends {}, MinorData extends {}>({
  className,
  strokeWidth = 2,
  stacks: majorStacks,
  minorStacks,
  activeSeries: majorActiveSeries,
  minorActiveSeries,
  firstTickValue,
  tickIntervalMinutes,
  resolution,
  stacked,
  comparisonLine,
  yAxisLabel,
  formatValue = defaultLineChartFormatValue,
  allowSingleXAxisValue = false,
  showLegend = false,
  'data-testid': dataTestId,
  tooltipLabelAs,
  hideCursorArrow = false,
}: {
  className?: string;
  strokeWidth?: number;
  minorStacks?: LineChartStackConfig<MinorData>[];
  minorActiveSeries?: boolean[];
  firstTickValue?: Moment;
  tickIntervalMinutes?: number;
  yAxisLabel?: string;
  comparisonLine?: { value: number; color: string; label?: string };
  formatValue?: (
    axis: 'major' | 'minor',
    value: number,
    maxValue: number,
  ) => string;
  allowSingleXAxisValue?: boolean;
  showLegend?: boolean;
  'data-testid'?: string;
  tooltipLabelAs?: React.ReactElement<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    string | React.JSXElementConstructor<any>
  >;
  hideCursorArrow?: boolean;
} & StackDataProps<MajorData>) {
  // Generates a unique chart ID, useful for managing DOM elements within SVG.
  const chartId = useMemo(() => uniqueId('step-chart'), []);

  // Calculate layout parameters for the SVG.
  // setMaxYLabelLength, setMaxLeftYLabelLength, setFirstTick are handlers to manage layout and ticks.
  const {
    setMaxYLabelLength,
    setMaxLeftYLabelLength,
    setFirstTick,
    ...svgLayout
  } = useSvgLayout({
    top: CHAR_HEIGHT,
    left: 24,
  });

  // Extract height, width, and bottom margin from the layout parameters.
  const { height, width, bottom } = svgLayout;

  // Calculate series data and maximum Y values for both major and minor data.
  // This is where the actual data series for the graph is generated and maximum Y-values are determined.
  // Note that this handles time-series data and accounts for both active and inactive series.
  const { series, majorMaxYValue, minorMaxYValue } = useMemo(() => {
    // Cache to store computed data records for the line chart
    const cache: Record<string, LineChartDataRecord> = {};

    // Calculate data and maximum value for major stacks
    const majorMaxValue = addStacksData({
      stacks: majorStacks,
      activeSeries: majorActiveSeries,
      resolution,
      cache,
      stacked,
      axis: 'major',
    });

    // Calculate data and maximum value for minor stacks, also consider the value of the comparison line
    const minorMaxValue = Math.max(
      comparisonLine?.value ?? 0,
      addStacksData({
        stacks: minorStacks ?? [],
        activeSeries: minorActiveSeries,
        resolution,
        cache,
        stacked,
        axis: 'minor',
      }),
    );
    // Return series data and maximum Y-values for major and minor data
    return {
      series: Object.values(cache),
      majorMaxYValue: majorMaxValue,
      minorMaxYValue: minorMaxValue,
    };
  }, [
    majorStacks,
    majorActiveSeries,
    resolution,
    stacked,
    comparisonLine?.value,
    minorStacks,
    minorActiveSeries,
  ]);

  /**
   * Use the custom hook useYAxisTicks to get tick values for the Y-axis.
   * These tick values are calculated based on the maximum Y-values for major and minor data.
   * It also sets the maximum Y label lengths for both sides of the chart.
   */
  const majorYAxisTicks = useYAxisTicks({
    height,
    maxValue: majorMaxYValue,
    setMaxYLabelLength,
    formatValue: useCallback(
      (value: number) => formatValue('major', value, majorMaxYValue),
      [formatValue, majorMaxYValue],
    ),
  });
  const minorYAxisTicks = useYAxisTicks({
    height,
    maxValue: minorMaxYValue,
    setMaxYLabelLength: setMaxLeftYLabelLength,
    formatValue: useCallback(
      (value: number) => formatValue('minor', value, minorMaxYValue),
      [formatValue, minorMaxYValue],
    ),
  });

  const majorYScale = useLinearScale({
    range: [height - strokeWidth / 2, 0],
    domain: [0, majorMaxYValue],
  });

  /**
   * Use linear scales for Y-axes of major and minor data.
   * This allows us to map data points in the series to actual pixel values in the SVG.
   */
  const minorYScale = useLinearScale({
    range: [height - strokeWidth / 2, 0],
    domain: [0, minorMaxYValue],
  });

  // Create an array to hold all stacks, both major and minor, for processing.
  const allStacks = useMemo(
    () =>
      [
        {
          stacks: majorStacks.map(
            ({ getValue, data, hasData, ...stack }, stackId) => ({
              ...stack,
              stackId,
            }),
          ),
          axis: 'major' as const,
          activeSeries: majorActiveSeries,
          yScale: majorYScale,
          axisLine: minorStacks && ('right' as const),
          maxYValue: majorMaxYValue,
        },
        {
          stacks:
            minorStacks?.map(
              ({ getValue, data, hasData, ...stack }, stackId) => ({
                ...stack,
                stackId,
              }),
            ) ?? [],
          axis: 'minor' as const,
          activeSeries: minorActiveSeries,
          yScale: minorYScale,
          axisLine: minorStacks && ('left' as const),
          maxYValue: minorMaxYValue,
        },
      ].filter(({ stacks }) => stacks.length > 0),
    [
      majorActiveSeries,
      majorMaxYValue,
      majorStacks,
      majorYScale,
      minorActiveSeries,
      minorMaxYValue,
      minorStacks,
      minorYScale,
    ],
  );

  /**
   * Calculate the minimum and maximum date in the time series.
   * This is crucial for setting up the domain of the X-axis scale.
   */
  const [minDate, maxDate] = useMemo(
    () => d3.extent(series, (bucket) => bucket.timestamp) as [Date, Date],
    [series],
  );

  /**
   * Use a time scale for the X-axis.
   * This allows us to map timestamp data points in the series to actual pixel values in the SVG.
   */
  const xScale = useTimeScale({
    range: [0, width],
    domain: [minDate, maxDate],
  });

  /**
   * Create an array of configuration objects for each stack that will be rendered.
   * This information includes scales, series data, and other relevant properties needed to render each stack.
   */
  const renderedStackInfos = useMemo(() => {
    return allStacks.map(({ stacks, activeSeries, ...stacksConfig }) =>
      stacks
        .filter((_stack, i) => !activeSeries || activeSeries[i])
        .map((stack) => ({
          height,
          xScale,
          series,
          ...stacksConfig,
          ...stack,
        }))
        .map(getRenderedStackInfo),
    );
  }, [allStacks, height, xScale, series]);

  /**
   * Generate legend keys if the showLegend flag is set.
   * This array is used to display the color-coded legend.
   */
  const legendKeys = useMemo(
    () =>
      showLegend
        ? allStacks.reduce((stackKeys: Key[], { stacks }) => {
            const temp = [...stackKeys];

            stacks.forEach((stack) => {
              if (stack?.label && stack?.primaryColor) {
                temp.push({
                  label: stack.label,
                  color: stack.primaryColor,
                });
              }
            });

            return temp;
          }, [])
        : [],
    [allStacks, showLegend],
  );

  /**
   * Custom hook to get the index of the series data that the cursor is currently over.
   * This is useful for rendering tooltips and other mouse-over interactions.
   */
  const cursorValueIndex = useCursorDateIndex({
    data: series,
    getTimestamp: (bucket: LineChartDataRecord) => bucket.timestamp,
  });

  /**
   * Compute the data to be shown when the cursor hovers over a point on the graph.
   * This includes tooltip content and coordinates for each active stack at that point.
   */
  const cursorValue = useMemo(() => {
    if (cursorValueIndex === null) return null;
    const value = series[cursorValueIndex];
    if (!value) return null;

    const values = _.orderBy(
      allStacks
        .map((info) => ({
          ...info,
          activeStackCounts: value[`${info.axis}StackCounts`].filter(
            (stackCount): stackCount is NonNullable<typeof stackCount> =>
              stackCount !== null &&
              (!info.activeSeries || !!info.activeSeries[stackCount.stackId]),
          ),
        }))
        .flatMap(({ stacks, activeStackCounts, axisLine }, i, arr) => {
          const stackInfo = renderedStackInfos[i];
          const tooltipContent = React.cloneElement(
            tooltipLabelAs ?? <LineChartTooltip />,
            {
              allStacks: arr,
              formatValue,
              value,
              resolution,
            },
          );

          return activeStackCounts.map(({ stackId }, activeIndex) => {
            const { line, primaryColor, cursorColor } =
              stackInfo?.[stackId] ?? {};
            const y = line?.y()(value, cursorValueIndex, series) ?? 0;
            return {
              axisLine,
              timestamp: value.timestamp,
              x: line?.x()(value, cursorValueIndex, series) ?? 0,
              y,
              primaryColor,
              cursorColor,
              tooltipContent,
              key: stacks[activeIndex]?.label ?? y,
            };
          });
        }),
      'y',
    );

    if (isMinLengthArray(1, values)) {
      const [firstValue, ...rest] = values;
      return [
        { ...firstValue, axisLine: firstValue?.axisLine ?? ('down' as const) },
        ...rest,
      ];
    } else {
      return values;
    }
  }, [
    allStacks,
    cursorValueIndex,
    formatValue,
    renderedStackInfos,
    resolution,
    series,
    tooltipLabelAs,
  ]);

  // Fetch the first data record from the series array and the first entry from renderedStackInfos.
  const firstSeriesRecord = series[0];
  const firstRenderedStackInfosArr = renderedStackInfos?.[0] ?? [];

  // Determine whether a single x-axis value should be shown based on provided conditions.
  const showSingleXAxisValue =
    allowSingleXAxisValue &&
    series.length === 1 &&
    firstSeriesRecord &&
    firstRenderedStackInfosArr.length > 0;

  const {
    ticks: xTicks,
    cursorTick,
    hiddenTickIndexs,
  } = useXTimeAxisTicks({
    scale: xScale,
    width,
    cursorTimestamp:
      (showSingleXAxisValue ? series : cursorValue)?.[0]?.timestamp ?? null,
    firstTickValue,
    tickIntervalMinutes,
    firstDate: series[0]?.timestamp,
    setFirstTick,
  });

  // If the series has only one data point and the chart is not configured to allow single x-axis values, return null.
  // (This chart doesn't make sense with only 1 data point, so treat anything less than 2 as empty)
  if (series.length <= 1 && !allowSingleXAxisValue) return null;

  // Checking if the chart is ready to be rendered by ensuring width and height are set.
  const readyToRender = width > 0 && height > 0;

  // Boolean flag to determine if there are multiple lines in the chart.
  const hasMultipleLines =
    allStacks.length > 1 ||
    majorStacks.length > 1 ||
    (minorStacks?.length || 0) > 1;

  // Rendering the chart
  return (
    <div className={className}>
      <div className="flex size-full">
        <div className="flex-grow">
          <SvgWrapper
            className="size-full"
            getCursor={(n) => ({ date: moment(xScale.invert(n[0])) })}
            data-testid={dataTestId}
            {...svgLayout}
          >
            <defs>
              {renderedStackInfos.map((renderedStackInfo) => {
                return renderedStackInfo.map(
                  ({
                    key,
                    colorGradientTop = 'transparent',
                    colorGradientBottom = colorGradientTop,
                    clipAreaPath,
                  }) => {
                    return (
                      <Fragment key={key}>
                        <linearGradient
                          id={`gradient-${key}-${chartId}`}
                          gradientTransform="rotate(90)"
                        >
                          <stop offset="20%" stopColor={colorGradientTop} />
                          <stop offset="100%" stopColor={colorGradientBottom} />
                        </linearGradient>
                        <clipPath
                          key={key}
                          id={`series-clip-${chartId}-${key}`}
                        >
                          <path d={clipAreaPath} />
                        </clipPath>
                      </Fragment>
                    );
                  },
                );
              })}
            </defs>
            <Axis
              scale={xScale}
              y={height}
              cursorTick={cursorTick}
              hiddenTicks={hiddenTickIndexs}
              tickValues={xTicks}
              side="bottom"
              cursorContrastMode="high"
            />
            <Axis
              withLines={true}
              tickValues={majorYAxisTicks}
              scale={majorYScale}
              x={width}
              hideDomain
              side="right"
            />
            {minorStacks && (
              <Axis
                tickValues={minorYAxisTicks}
                scale={minorYScale}
                hideDomain
                side="left"
              />
            )}

            {readyToRender &&
              renderedStackInfos.map((renderedStackInfo) => {
                return renderedStackInfo.map((stack) => {
                  return (
                    <Fragment key={stack.key}>
                      {stack.colorGradientBottom && (
                        <rect
                          clipPath={`url(#series-clip-${chartId}-${stack.key})`}
                          y={-margins.top}
                          x={0}
                          width={width}
                          height={height + margins.top}
                          className={classnames(
                            hasMultipleLines && !stacked
                              ? 'opacity-0 hover:opacity-40'
                              : '',
                          )}
                          fill={customColors.bg.primary}
                          d={stack.areaPath}
                        />
                      )}
                      <rect
                        clipPath={`url(#series-clip-${chartId}-${stack.key})`}
                        y={-margins.top}
                        x={0}
                        width={width}
                        height={height + margins.top}
                        className={classnames(
                          hasMultipleLines && !stacked
                            ? 'opacity-0 hover:opacity-40'
                            : '',
                        )}
                        fill={`url(#gradient-${stack.key}-${chartId})`}
                        d={stack.areaPath}
                      />
                      <path
                        d={stack.d3LinePath}
                        fill="none"
                        stroke={stack.primaryColor}
                        strokeWidth={strokeWidth}
                        css={css`
                          transition-property: d;
                          transition-timing-function: cubic-bezier(
                            0.4,
                            0,
                            0.2,
                            1
                          );
                          transition-duration: 150ms;
                        `}
                      />
                    </Fragment>
                  );
                });
              })}
            {comparisonLine && readyToRender && (
              <>
                <line
                  stroke={comparisonLine.color}
                  x2={width}
                  strokeDasharray={4}
                  transform={`translate(0, ${minorYScale(
                    comparisonLine.value,
                  )})`}
                  className="transition-transform"
                />
                {comparisonLine.label && (
                  <text
                    fill={comparisonLine.color}
                    transform={`translate(0, ${
                      minorYScale(comparisonLine.value) - 3
                    })`}
                    textAnchor="bottom"
                    className="text-xs font-semibold transition-transform"
                  >
                    {comparisonLine.label}
                  </text>
                )}
              </>
            )}
            {showSingleXAxisValue
              ? firstRenderedStackInfosArr.map((renderedStackInfo) => (
                  <circle
                    key={renderedStackInfo.key}
                    transform={`translate(${
                      renderedStackInfo.line.x()(
                        firstSeriesRecord,
                        0,
                        series,
                      ) ?? 0
                    }, ${
                      renderedStackInfo.line.y()(
                        firstSeriesRecord,
                        0,
                        series,
                      ) ?? 0
                    })`}
                    r={2.5}
                    fill={renderedStackInfo.primaryColor}
                    stroke={renderedStackInfo.primaryColor}
                  />
                ))
              : null}
            {cursorValue?.map((v) => (
              <Cursor
                key={v.key}
                cursorValue={v}
                dotColor={v.cursorColor ?? v.primaryColor}
                axisLine={v.axisLine}
                width={width}
                height={height}
                showArrow={!hideCursorArrow}
                showDot
              />
            ))}
          </SvgWrapper>
          {legendKeys.length ? (
            <Legend className="mt-6" keys={legendKeys} />
          ) : null}
        </div>
        {yAxisLabel ? (
          <YAxisLabel height={height - bottom} value={yAxisLabel} />
        ) : null}
      </div>
    </div>
  );
}
